<template>
  <div class="booking-summary-report">
        <!-- Filters -->
        <b-card class="mb-4">
            <b-card-header class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">
                    <i class="fas fa-filter mr-2"></i>Filters
                </h5>
                <div class="">
                  <b-button variant="outline-primary" @click="exportReport('csv')" :disabled="loading" class="mr-2">
                    <i class="fas fa-download mr-2"></i>Export CSV
                  </b-button>
                  <b-button variant="outline-secondary" @click="exportReport('pdf')" :disabled="loading">
                    <i class="fas fa-file-pdf mr-2"></i>Export PDF
                  </b-button>
                </div>
            </b-card-header>
            <b-card-body>
                <b-form @submit.prevent="fetchReport">
                    <b-row>
                        <b-col md="3">
                            <b-form-group label="Date From:">
                                <b-form-datepicker
                                    v-model="filters.date_from"
                                    placeholder="Select start date"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col md="3">
                            <b-form-group label="Date To:">
                                <b-form-datepicker
                                    v-model="filters.date_to"
                                    placeholder="Select end date"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col md="3">
                            <b-form-group label="Hotel:">
                                <b-form-select
                                    v-model="filters.hotel_id"
                                    :options="hotelOptions"
                                    :disabled="loadingOptions"
                                >
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col md="3">
                            <b-form-group label="Status:">
                                <b-form-select
                                    v-model="filters.status"
                                    :options="statusOptions"
                                >
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="3">
                            <b-form-group label="Payment Status:">
                                <b-form-select
                                    v-model="filters.payment_status"
                                    :options="paymentStatusOptions"
                                >
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                      </b-row>
                      <b-row class="mt-3">
                        <b-col md="6" class="d-flex align-items-end">
                            <b-button type="submit" variant="primary" :disabled="loading" class="mr-2">
                                <i class="fas fa-search mr-2"></i>Apply Filters
                            </b-button>
                            <b-button variant="outline-secondary" @click="resetFilters" :disabled="loading">
                                <i class="fas fa-times mr-2"></i>Clear
                            </b-button>
                        </b-col>
                    </b-row>
                </b-form>
            </b-card-body>
        </b-card>

        <!-- Results Table -->
        <b-card>
            <b-card-header>
                <h5 class="mb-0">
                    <i class="fas fa-table mr-2"></i>
                    Booking Summary Results
                </h5>
            </b-card-header>
            <b-card-body class="p-0">
                <CrudTable
                    :columns="tableFields"
                    :data-sources="reportData.data"
                    :pagination="paginationInfo"
                    :loader="loading"
                    list-url="/hotel-reports/booking-summary"
                    @search="handleSearch"
                    @filter="handleFilter"
                    @refresh="fetchReport"
                >
                    <!-- Custom cell templates -->
                    <template #cell(booking_id)="data">
                        <strong class="text-primary">#{{ data.item.booking_id }}</strong>
                    </template>

                    <template #cell(room_types)="data">
                        <div v-if="data.item.room_types && data.item.room_types.length">
                            <div v-for="(roomType, index) in data.item.room_types" :key="index" class="small">
                                <strong>{{ roomType.name }}</strong>
                                <span class="text-muted">({{ roomType.count }} room{{ roomType.count > 1 ? 's' : '' }})</span>
                            </div>
                        </div>
                        <span v-else class="text-muted">N/A</span>
                    </template>

                    <template #cell(total_amount)="data">
                        <strong class="text-success">${{ Number(data.item.total_amount).toFixed(2) }}</strong>
                    </template>

                    <template #cell(payment_status)="data">

                        <b-badge :variant="getPaymentStatusVariant(data.item.payment_status)">
                            {{ data.item.payment_status }}
                        </b-badge>
                    </template>

                    <template #cell(reservation_status)="data">
                        <b-badge :variant="getReservationStatusVariant(data.item.reservation_status)">
                            {{ data.item.reservation_status }}
                        </b-badge>
                    </template>

          <template #cell(booking_date)="data">
            {{ formatDate(data.item.booking_date) }}
          </template>

          <template #cell(check_in)="data">
            {{ formatDate(data.item.check_in) }}
          </template>

                    <template #cell(check_out)="data">
                        {{ formatDate(data.item.check_out) }}
                    </template>
                </CrudTable>
            </b-card-body>
        </b-card>
    </div>
</template>

<script>
import { request } from '@/Util/Request';
import CrudTable from '@/components/CrudTable.vue';

export default {
    name: 'BookingSummaryReport',
    components: {
        CrudTable
    },
    data() {
    return {
        loading: false,
        loadingOptions: false,
        currentPage: 1,
        filters: {
            date_from: '',
            date_to: '',
            hotel_id: null,
            status: null,
            payment_status: null,
            per_page: 15
        },
        reportData: {
            data: [],
            total: 0,
            current_page: 1,
            last_page: 1
        },
        paginationInfo: {
            current_page: 1,
            per_page: 15,
            total: 0,
            from: 0,
            to: 0,
            last_page: 1
        },
        perPageOptions: [
            { value: 10, text: '10 per page' },
            { value: 15, text: '15 per page' },
            { value: 25, text: '25 per page' },
            { value: 50, text: '50 per page' }
        ],
        tableFields: [
            { key: 'booking_id', label: 'Booking ID', sortable: true },
            { key: 'guest_name', label: 'Guest Name', sortable: true },
            { key: 'email', label: 'Email' },
            { key: 'hotel', label: 'Hotel' },
            { key: 'room_types', label: 'Room Types' },
            { key: 'check_in', label: 'Check-in' },
            { key: 'check_out', label: 'Check-out' },
            { key: 'total_amount', label: 'Total Amount', sortable: true },
            { key: 'payment_status', label: 'Payment Status' },
            { key: 'reservation_status', label: 'Status' },
            { key: 'booking_date', label: 'Booking Date', sortable: true }
        ]
    };
  },
  props: {
    hotelOptions:{
      type: Array,
      required:true
    },
    statusOptions: {
      type: Array,
      required:true
    },
    paymentStatusOptions: {
      type: Array,
      required:true
    },
  },
  async mounted() {
    console.log(this.paymentStatusOptions);
    
    // await this.fetchFilterOptions();
    await this.fetchReport();
  },
  methods: {
    async fetchReport() {
      try {
        this.loading = true;
        const params = {
          ...this.filters,
          page: this.currentPage
        };

        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key];
          }
        });

        const response = await request({
          method: 'get',
          url: '/hotel-reports/booking-summary',
          params
        });

                this.reportData = response.data;
                this.paginationInfo = {
                    current_page: response.current_page,
                    per_page: response.per_page,
                    total: response.total,
                    from: response.from,
                    to: response.to,
                    last_page: response.last_page
                };
                // console.log('Report Data:', this.reportData);
            } catch (error) {
                console.error('Error fetching booking summary:', error);
                this.$bvToast.toast('Failed to load booking summary report', {
                    title: 'Error',
                    variant: 'danger'
                });
            } finally {
                this.loading = false;
            }
        },

    handleSearch() {
        this.fetchReport();
    },

    handleFilter() {
        this.fetchReport();
    },

    handlePageChange(page) {
        this.currentPage = page;
        this.fetchReport();
    },

    handlePerPageChange() {
      this.currentPage = 1;
      this.fetchReport();
    },

    resetFilters() {
      this.filters = {
        date_from: '',
        date_to: '',
        hotel_id: '',
        status: '',
        payment_status: '',
        per_page: 15
      };
      this.currentPage = 1;
      this.fetchReport();
    },

    async exportReport(format) {
      try {
        this.loading = true;

        // Prepare export parameters
        const params = {
          ...this.filters,
          format: format
        };

        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key];
          }
        });

        const response = await request({
          method: 'get',
          url: '/hotel-reports/booking-summary-export',
          params: params,
          responseType: 'blob'
        });

        // Create download link
        const mimeType = format === 'csv'
          ? 'text/csv'
          : 'application/pdf';

        const blob = new Blob([response], { type: mimeType });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `booking_summary_report.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        this.$bvToast.toast(`Report exported successfully as ${format.toUpperCase()}`, {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Export failed:', error);
        this.$bvToast.toast('Failed to export report', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },

    getPaymentStatusVariant(status) {
      switch (status) {
          case 'paid': return 'badge bg-success text-white';
          case 'pending': return 'badge bg-warning text-dark';
          case 'failed': return 'badge bg-danger text-white';
          case 'cancelled': return 'badge bg-secondary text-white';
          default: return 'badge bg-secondary text-white';
      }
    },

    getReservationStatusVariant(status) {
      switch (status) {
        case 'booked': return 'badge bg-primary text-white';
        case 'checked_in': return 'badge bg-warning text-dark';
        case 'completed': return 'badge bg-success text-white';
        case 'cancelled': return 'badge bg-secondary text-white';
        default: return 'badge bg-secondary text-white';
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString();
    }
  }
};
</script>

<style scoped>
/* .booking-summary-report {
  padding: 20px;
} */

.table th {
  background-color: #f8f9fa;
  border-top: none;
}

.badge {
  font-size: 0.85em;
  text-transform: capitalize;
}

.bg-success {
    background: #22c55e !important;
    color: #fff !important;
}

.bg-warning {
    background: #fbbf24 !important;
    color: #222 !important;
}

.bg-danger {
    background: #ef4444 !important;
    color: #fff !important;
}

.bg-secondary {
    background: #6b7280 !important;
    color: #fff !important;
}
.bg-primary {
    background: #0d6efd !important;
    color: #fff !important;
}
</style>
